import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Link,
    Slider,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    DeleteForever as DeletePhotoIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/cs';
import type { Technika } from '../../api/JsdhNotifikaceService/generated/models/Technika';
import type { TechnikaRequest } from '../../api/JsdhNotifikaceService/generated/models/TechnikaRequest';
import type { KontrolaStatusResponse } from '../../api/JsdhNotifikaceService/generated/models/Kontrola';
import type { MetadataFieldSchema } from '../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import type { TechnikaKontrola, CreateTechnikaKontrolaRequest } from '../../api/JsdhNotifikaceService/generated/models/TechnikaKontrola';
import {
    useTechnikaByIdQuery,
    useKnihaJizdQuery,
    useKontrolyForTechnikaQuery,
    useTechnikaKontrolyHistorieQuery,
    useCreateTechnikaKontrolaMutation,
    useUpdateTechnikaKontrolaMutation,
    useDeleteTechnikaKontrolaMutation,
    useUpdateTechnikaMutation,
    useUploadFotografieMutation,
    useDeleteFotografieMutation,
} from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import { axiosInstance } from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { TECHNIKA_FOTOGRAFIE_URL } from '../../api/JsdhNotifikaceService/url';
import TechnikaParovaniInfo from '../../components/admin/TechnikaParovaniInfo';

dayjs.locale('cs');

// ==========================================
// HELPER: Status Chip
// ==========================================

const StatusChip: React.FC<{ status: KontrolaStatusResponse }> = ({ status }) => {
    switch (status.status) {
        case 'PLATNA':
            return (
                <Chip
                    color="success"
                    label={`Platná do ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'BLIZI_SE_EXPIRACE':
            return (
                <Chip
                    color="warning"
                    label={`Vyprší ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'EXPIROVANA':
            return (
                <Chip
                    color="error"
                    label={`Expirovaná ${dayjs(status.datumExpirace).format('DD.MM.YYYY')}`}
                    size="small"
                />
            );
        case 'CHYBI':
            return <Chip variant="outlined" label="Chybí" size="small" />;
        default:
            return null;
    }
};

// ==========================================
// DIALOG: Zaznamenat kontrolu techniky
// ==========================================

const TechnikaKontrolaDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    technikaId: number;
    technikaNazev: string;
    kontrolaStatus: KontrolaStatusResponse;
}> = ({ open, onClose, technikaId, technikaNazev, kontrolaStatus }) => {
    const [datumSplneni, setDatumSplneni] = useState<Dayjs | null>(dayjs());
    const [poznamka, setPoznamka] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);

    const createMutation = useCreateTechnikaKontrolaMutation();
    const updateMutation = useUpdateTechnikaKontrolaMutation();
    const deleteMutation = useDeleteTechnikaKontrolaMutation();
    const { data: historie } = useTechnikaKontrolyHistorieQuery(
        open ? technikaId : null,
        open ? kontrolaStatus.typKontrolyId : null
    );

    const metadataSchema: MetadataFieldSchema[] = useMemo(() => {
        if (!kontrolaStatus.metadataSchema) return [];
        try {
            return JSON.parse(kontrolaStatus.metadataSchema);
        } catch {
            return [];
        }
    }, [kontrolaStatus.metadataSchema]);

    const handleReset = () => {
        setDatumSplneni(dayjs());
        setPoznamka('');
        setEditingId(null);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleEdit = (record: TechnikaKontrola) => {
        setDatumSplneni(dayjs(record.datumSplneni));
        setPoznamka(record.poznamka || '');
        setEditingId(record.id);
    };

    const handleSave = () => {
        if (!datumSplneni) return;

        const request: CreateTechnikaKontrolaRequest = {
            technikaId,
            typKontrolyId: kontrolaStatus.typKontrolyId,
            datumSplneni: datumSplneni.format('YYYY-MM-DD'),
            poznamka: poznamka || undefined,
        };

        if (editingId) {
            updateMutation.mutate({ kontrolaId: editingId, technikaId, data: request }, {
                onSuccess: () => handleReset(),
            });
        } else {
            createMutation.mutate({ technikaId, data: request }, {
                onSuccess: () => handleReset(),
            });
        }
    };

    const handleDelete = (record: TechnikaKontrola) => {
        if (window.confirm('Opravdu smazat tento záznam?')) {
            deleteMutation.mutate({ kontrolaId: record.id, technikaId });
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {kontrolaStatus.typKontrolyNazev} — {technikaNazev}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <DatePicker
                        label="Datum splnění"
                        value={datumSplneni}
                        onChange={setDatumSplneni}
                        format="DD.MM.YYYY"
                        slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'dense' } }}
                    />

                    <TextField
                        label="Poznámka"
                        value={poznamka}
                        onChange={(e) => setPoznamka(e.target.value)}
                        fullWidth
                        margin="dense"
                        size="small"
                        multiline
                        rows={2}
                    />
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!datumSplneni || isSaving}
                        size="small"
                    >
                        {isSaving ? <CircularProgress size={20} /> : editingId ? 'Uložit změny' : 'Zaznamenat'}
                    </Button>
                    {editingId && (
                        <Button variant="outlined" onClick={handleReset} size="small">
                            Zrušit editaci
                        </Button>
                    )}
                </Box>

                {historie && historie.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Historie</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Datum splnění</TableCell>
                                        <TableCell>Expirace</TableCell>
                                        {metadataSchema.map((field) => (
                                            <TableCell key={field.key}>{field.label}</TableCell>
                                        ))}
                                        <TableCell>Poznámka</TableCell>
                                        <TableCell align="right">Akce</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historie.map((record) => {
                                        let meta: Record<string, any> = {};
                                        if (record.metadata) {
                                            try { meta = JSON.parse(record.metadata); } catch { /* ignore */ }
                                        }
                                        return (
                                            <TableRow key={record.id}>
                                                <TableCell>{dayjs(record.datumSplneni).format('DD.MM.YYYY')}</TableCell>
                                                <TableCell>{dayjs(record.datumExpirace).format('DD.MM.YYYY')}</TableCell>
                                                {metadataSchema.map((field) => (
                                                    <TableCell key={field.key}>
                                                        {meta[field.key] != null ? String(meta[field.key]) : '—'}
                                                    </TableCell>
                                                ))}
                                                <TableCell>{record.poznamka || '—'}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Upravit">
                                                        <IconButton size="small" onClick={() => handleEdit(record)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Smazat">
                                                        <IconButton size="small" onClick={() => handleDelete(record)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

// ==========================================
// DIALOG: Editace techniky
// ==========================================

const TechnikaEditDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    technika: Technika;
}> = ({ open, onClose, technika }) => {
    const updateMutation = useUpdateTechnikaMutation();
    const uploadFotoMutation = useUploadFotografieMutation();
    const deleteFotoMutation = useDeleteFotografieMutation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fotoPreview, setFotoPreview] = useState<string | null>(null);

    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [cropImage, setCropImage] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const [formData, setFormData] = useState<TechnikaRequest>({
        volaciZnak: technika.volaciZnak,
        nazev: technika.nazev,
        spz: technika.spz || '',
        aktivni: technika.aktivni,
    });

    useEffect(() => {
        setFormData({
            volaciZnak: technika.volaciZnak,
            nazev: technika.nazev,
            spz: technika.spz || '',
            aktivni: technika.aktivni,
        });
        if (technika.maFotografii) {
            axiosInstance.get(TECHNIKA_FOTOGRAFIE_URL(technika.id), { responseType: 'blob' })
                .then(res => setFotoPreview(URL.createObjectURL(res.data)))
                .catch(() => setFotoPreview(null));
        } else {
            setFotoPreview(null);
        }
    }, [technika]);

    const handleSubmit = () => {
        updateMutation.mutate(
            { id: technika.id, data: formData },
            { onSuccess: onClose }
        );
    };

    const handleFotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) return;
            const reader = new FileReader();
            reader.onload = () => {
                setCropImage(reader.result as string);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                setCropDialogOpen(true);
            };
            reader.readAsDataURL(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<File> => {
        const image = await new Promise<HTMLImageElement>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = imageSrc;
        });
        const canvas = document.createElement('canvas');
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(new File([blob!], 'fotografie.jpg', { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.9);
        });
    };

    const handleCropConfirm = async () => {
        if (!cropImage || !croppedAreaPixels) return;
        const croppedFile = await getCroppedImg(cropImage, croppedAreaPixels);
        const previewUrl = URL.createObjectURL(croppedFile);
        uploadFotoMutation.mutate(
            { id: technika.id, file: croppedFile },
            {
                onSuccess: () => {
                    setFotoPreview(previewUrl);
                    setCropDialogOpen(false);
                    setCropImage(null);
                },
            }
        );
    };

    const handleFotoDelete = () => {
        deleteFotoMutation.mutate(technika.id, {
            onSuccess: () => setFotoPreview(null),
        });
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Upravit techniku</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TechnikaParovaniInfo />
                        <TextField
                            label="Volací znak"
                            value={formData.volaciZnak}
                            onChange={(e) => setFormData({ ...formData, volaciZnak: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 50 }}
                            helperText="Podle volacího znaku se vozidlo primárně páruje s poplachem (např. HPE 542)"
                        />
                        <TextField
                            label="Název"
                            value={formData.nazev}
                            onChange={(e) => setFormData({ ...formData, nazev: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            helperText="Zapište přesně tak, jak název posílá KOPIS (např. CAS 32/8200/800-S3R T815)"
                        />
                        <TextField
                            label="SPZ"
                            value={formData.spz || ''}
                            onChange={(e) => setFormData({ ...formData, spz: e.target.value })}
                            fullWidth
                            inputProps={{ maxLength: 20 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.aktivni ?? true}
                                    onChange={(e) => setFormData({ ...formData, aktivni: e.target.checked })}
                                />
                            }
                            label="Aktivní"
                        />

                        {/* Photo */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Fotografie</Typography>
                            {fotoPreview ? (
                                <Box>
                                    <Box
                                        component="img"
                                        src={fotoPreview}
                                        alt="Fotografie techniky"
                                        sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, display: 'block' }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Button size="small" startIcon={<CloudUploadIcon />} onClick={() => fileInputRef.current?.click()}>
                                            Nahrát novou
                                        </Button>
                                        <Button size="small" color="error" startIcon={<DeletePhotoIcon />} onClick={handleFotoDelete} disabled={deleteFotoMutation.isPending}>
                                            Smazat
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            width: 120, height: 90,
                                            border: '2px dashed', borderColor: 'grey.400', borderRadius: 2,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', bgcolor: 'grey.50',
                                            '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.100' },
                                        }}
                                    >
                                        <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                                    </Box>
                                </Box>
                            )}
                            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" onChange={handleFotoUpload} style={{ display: 'none' }} />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!formData.volaciZnak.trim() || !formData.nazev.trim() || updateMutation.isPending}
                    >
                        {updateMutation.isPending ? <CircularProgress size={20} /> : 'Uložit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Crop Dialog */}
            <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Oříznout fotografii</DialogTitle>
                <DialogContent sx={{ position: 'relative', height: 400, p: 0 }}>
                    {cropImage && (
                        <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={4 / 3}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ flex: 1, px: 2 }}>
                        <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, v) => setZoom(v as number)} />
                    </Box>
                    <Button onClick={() => setCropDialogOpen(false)}>Zrušit</Button>
                    <Button variant="contained" onClick={handleCropConfirm} disabled={uploadFotoMutation.isPending}>
                        Oříznout a nahrát
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ==========================================
// MAIN: Detail Technika Page
// ==========================================

const DetailTechnikaPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const technikaId = id ? parseInt(id, 10) : null;

    const { data: technika, isLoading, error } = useTechnikaByIdQuery(technikaId!);
    const { data: kontroly } = useKontrolyForTechnikaQuery(technikaId);
    const { data: jizdy } = useKnihaJizdQuery(technikaId);

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [kontrolaDialogState, setKontrolaDialogState] = useState<{
        open: boolean;
        kontrolaStatus: KontrolaStatusResponse;
    } | null>(null);

    const [fotoSrc, setFotoSrc] = useState<string | null>(null);
    const [fotoVersion, setFotoVersion] = useState(0);

    useEffect(() => {
        if (technika?.maFotografii && technikaId) {
            axiosInstance.get(TECHNIKA_FOTOGRAFIE_URL(technikaId), { responseType: 'blob' })
                .then(res => setFotoSrc(URL.createObjectURL(res.data)))
                .catch(() => setFotoSrc(null));
        } else {
            setFotoSrc(null);
        }
    }, [technika, technikaId, fotoVersion]);

    const celkemJizd = jizdy?.length || 0;
    const celkemKm = useMemo(() => jizdy?.reduce((acc, j) => acc + (j.km || 0), 0) || 0, [jizdy]);
    const posledniRidic = jizdy && jizdy.length > 0 ? jizdy[0].uzivatelJmeno : null;

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Chyba při načítání techniky: {error.message}</Alert>;
    }

    if (!technika) {
        return <Alert severity="warning">Technika nenalezena.</Alert>;
    }

    return (
        <Box>
            {/* Header */}
            <PageHeader
                title={`${technika.volaciZnak} — ${technika.nazev}`}
                icon={
                    <IconButton onClick={() => navigate('/admin/technika')}>
                        <ArrowBackIcon />
                    </IconButton>
                }
                action={
                    <>
                        <Chip
                            label={technika.aktivni ? 'Aktivní' : 'Neaktivní'}
                            color={technika.aktivni ? 'success' : 'default'}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => setEditDialogOpen(true)}
                        >
                            Upravit
                        </Button>
                    </>
                }
            />

            {/* Content grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, sm: 3 }, mb: 3 }}>
                {/* Základní informace */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" gutterBottom>Základní informace</Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: { xs: 120, sm: 180 } }}>Volací znak</TableCell>
                                <TableCell>{technika.volaciZnak}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Název</TableCell>
                                <TableCell>{technika.nazev}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>SPZ</TableCell>
                                <TableCell>{technika.spz || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Stav</TableCell>
                                <TableCell>
                                    <Chip
                                        label={technika.aktivni ? 'Aktivní' : 'Neaktivní'}
                                        color={technika.aktivni ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Paper>

                {/* Fotografie + souhrn */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" gutterBottom>Fotografie</Typography>
                    {fotoSrc ? (
                        <Box
                            component="img"
                            src={fotoSrc}
                            alt={technika.nazev}
                            sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1, display: 'block', mb: 2 }}
                        />
                    ) : (
                        <Typography color="text.secondary" sx={{ mb: 2 }}>Bez fotografie</Typography>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        <Card variant="outlined" sx={{ textAlign: 'center' }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="h4">{celkemJizd}</Typography>
                                <Typography variant="body2" color="text.secondary">Jízd celkem</Typography>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ textAlign: 'center' }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="h4">{celkemKm}</Typography>
                                <Typography variant="body2" color="text.secondary">Km celkem</Typography>
                            </CardContent>
                        </Card>
                        <Card variant="outlined" sx={{ textAlign: 'center' }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{posledniRidic || '–'}</Typography>
                                <Typography variant="body2" color="text.secondary">Poslední řidič</Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Paper>
            </Box>

            {/* Kontroly techniky */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Kontroly techniky</Typography>
                {kontroly && kontroly.length > 0 ? (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Typ kontroly</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Poslední splnění</TableCell>
                                    <TableCell>Expirace</TableCell>
                                    <TableCell align="right">Akce</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {kontroly.map((kontrola) => (
                                    <TableRow key={kontrola.typKontrolyId}>
                                        <TableCell>{kontrola.typKontrolyNazev}</TableCell>
                                        <TableCell>
                                            <StatusChip status={kontrola} />
                                        </TableCell>
                                        <TableCell>
                                            {kontrola.datumSplneni
                                                ? dayjs(kontrola.datumSplneni).format('DD.MM.YYYY')
                                                : '–'}
                                        </TableCell>
                                        <TableCell>
                                            {kontrola.datumExpirace
                                                ? dayjs(kontrola.datumExpirace).format('DD.MM.YYYY')
                                                : '–'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setKontrolaDialogState({
                                                    open: true,
                                                    kontrolaStatus: kontrola,
                                                })}
                                            >
                                                Zaznamenat / Historie
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography color="text.secondary">
                        {kontroly ? 'Žádné typy kontrol pro techniku.' : 'Načítání kontrol...'}
                    </Typography>
                )}
            </Paper>

            {/* Kniha jízd */}
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>Kniha jízd</Typography>
                {jizdy && jizdy.length > 0 ? (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Datum</TableCell>
                                    <TableCell>Řidič</TableCell>
                                    <TableCell>Km</TableCell>
                                    <TableCell>Trasa</TableCell>
                                    <TableCell>Tachometr před</TableCell>
                                    <TableCell>Tachometr po</TableCell>
                                    <TableCell>Poznámka</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jizdy.map((jizda) => (
                                    <TableRow key={jizda.id}>
                                        <TableCell>{dayjs(jizda.datumSplneni).format('DD.MM.YYYY')}</TableCell>
                                        <TableCell>
                                            <Link
                                                component={RouterLink}
                                                to={`/admin/users/${jizda.uzivatelId}`}
                                                underline="hover"
                                            >
                                                {jizda.uzivatelJmeno}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{jizda.km ?? '–'}</TableCell>
                                        <TableCell>{jizda.trasa || '–'}</TableCell>
                                        <TableCell>{jizda.tachometrPred ?? '–'}</TableCell>
                                        <TableCell>{jizda.tachometrPo ?? '–'}</TableCell>
                                        <TableCell>{jizda.poznamka || '–'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography color="text.secondary">
                        {jizdy ? 'Žádné záznamy jízd.' : 'Načítání knihy jízd...'}
                    </Typography>
                )}
            </Paper>

            {/* Dialogy */}
            {editDialogOpen && (
                <TechnikaEditDialog
                    open={editDialogOpen}
                    onClose={() => {
                        setEditDialogOpen(false);
                        setFotoVersion(v => v + 1);
                    }}
                    technika={technika}
                />
            )}

            {kontrolaDialogState && (
                <TechnikaKontrolaDialog
                    open={kontrolaDialogState.open}
                    onClose={() => setKontrolaDialogState(null)}
                    technikaId={technikaId!}
                    technikaNazev={`${technika.volaciZnak} — ${technika.nazev}`}
                    kontrolaStatus={kontrolaDialogState.kontrolaStatus}
                />
            )}
        </Box>
    );
};

export default DetailTechnikaPage;
